import { atom, SetStateAction, WritableAtom } from 'jotai';
import { RESET } from 'jotai/utils';

export function atomWithSearch<Value>(
  key: string,
  initialValue: Value,
  options?: {
    serialize?: (val: Value) => string;
    deserialize?: (str: string) => Value;
    subscribe?: (callback: () => void) => () => void;
    setSearch?: 'default' | 'replaceState' | ((searchParams: string) => void);
  },
) {
  const serialize = options?.serialize || JSON.stringify;
  const deserialize = options?.deserialize || JSON.parse;
  const subscribe =
    options?.subscribe ||
    ((callback) => {
      window.addEventListener('replaceState', callback);
      return () => {
        window.removeEventListener('replaceState', callback);
      };
    });
  const setSearchOption = options?.setSearch;
  let setSearch = (searchParams: string) => {
    window.location.search = searchParams;
  };
  if (setSearchOption === 'replaceState') {
    setSearch = (searchParams) => {
      window.history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);
    };
  }
  if (typeof setSearchOption === 'function') {
    setSearch = setSearchOption;
  }
  const strAtom = atom<string | null>(null);
  strAtom.onMount = (setAtom) => {
    if (typeof window === 'undefined' || typeof window.location === 'undefined') {
      return undefined;
    }
    const wrapHistoryMethod = (type: 'pushState' | 'replaceState') => {
      const original = window.history[type];
      type Args = [data: unknown, unused: string, url?: string | URL | null | undefined];
      return function (this: History, ...args: Args) {
        const returnValue = original.apply(this, args);
        const event = new CustomEvent(type);
        (event as unknown as { arguments: Args }).arguments = args;
        window.dispatchEvent(event);
        return returnValue;
      };
    };
    window.history.replaceState = wrapHistoryMethod('replaceState');

    const callback = () => {
      const searchParams = new URLSearchParams(window.location.search.slice(1));
      const str = searchParams.get(key);
      setAtom(str);
    };
    const unsubscribe = subscribe(callback);
    callback();
    return unsubscribe;
  };
  const valueAtom = atom((get) => {
    const str = get(strAtom);
    return str === null ? initialValue : deserialize(str);
  });

  const searchAtom = atom(
    (get) => get(valueAtom),
    (get, set, update: SetStateAction<Value>) => {
      const nextValue =
        typeof update === 'function' ? (update as (prev: Value) => Value | typeof RESET)(get(valueAtom)) : update;
      const searchParams = new URLSearchParams(window.location.search.slice(1));
      if (nextValue === RESET) {
        set(strAtom, null);
        searchParams.delete(key);
      } else {
        const str = serialize(nextValue);
        set(strAtom, str);
        searchParams.set(key, str);
      }
      setSearch(searchParams.toString());
    },
  ) as unknown as WritableAtom<Value, SetStateAction<Value> | typeof RESET> & { originAtom: typeof strAtom };
  searchAtom.originAtom = strAtom;
  return searchAtom;
}
